/**
 * WorkflowSectionEditor
 *
 * Editor for the "Section Entry" node — a named entry point in the workflow
 * that AI nodes can jump to based on detected intent.
 *
 * Place this node at the start of a section (e.g. "Returns Process").
 * AI nodes configured with this section as a jump target will inject a
 * jump_to_{name} tool into their prompt, letting the AI route here when
 * the conversation matches the described intent.
 *
 * After the section runs, add a Transfer to AI node to return control
 * to the original AI (or any other AI).
 */
import * as React from "react";
import { FormGroup } from "../RightAsideHelpers";
import { WorkflowNode } from "../../types";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function WorkflowSectionEditor({ node, onUpdate }: Props) {
  return (
    <>
      {/* How it works banner */}
      <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800 leading-relaxed">
        <p className="font-semibold mb-1">How Section Entry works</p>
        <ol className="list-decimal list-inside space-y-1 text-amber-700">
          <li>Name this section (e.g. "Returns Process").</li>
          <li>
            In an AI node, add this section as a{" "}
            <span className="font-medium">Jump Target</span> and describe when to jump here.
          </li>
          <li>The AI detects the intent and jumps to this node.</li>
          <li>
            Add a <span className="font-medium">Transfer to AI</span> node at the end of this
            section to return control.
          </li>
        </ol>
      </div>

      {/* Section Name */}
      <FormGroup label="Section Name">
        <input
          type="text"
          className="input text-sm"
          placeholder='e.g. "Returns Process", "Billing Support", "Order Lookup"'
          value={node.sectionName ?? ""}
          onChange={(e) => onUpdate({ sectionName: e.target.value })}
        />
        <p className="text-xs text-slate-500 mt-1">
          This name appears in AI nodes' Jump Target selector and is used to generate the
          AI tool name (e.g. <code className="bg-slate-100 px-1 rounded font-mono">jump_to_returns_process</code>).
        </p>
      </FormGroup>

      {/* Section Description */}
      <FormGroup label="Description">
        <textarea
          className="input text-sm resize-none h-20"
          placeholder="What does this section handle? This helps document the workflow."
          value={node.sectionDescription ?? ""}
          onChange={(e) => onUpdate({ sectionDescription: e.target.value })}
        />
      </FormGroup>

      {/* Return path hint */}
      <div className="mt-2 bg-blue-50 border border-blue-200 rounded px-3 py-2 text-xs text-blue-700">
        <p className="font-medium mb-0.5">Return path</p>
        <p>
          End this section with a <span className="font-medium">Transfer to AI</span> node to
          hand control back to the calling AI. You can also end with{" "}
          <span className="font-medium">Transfer to Agents</span> or{" "}
          <span className="font-medium">Close Conversation</span>.
        </p>
      </div>
    </>
  );
}
